import { ModuleLayout } from '@billy/design-system'
import { ComponentType, createElement, Fragment, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router'

import { ReportsType } from '@features/reports/enums/reportsType'
import { BalanceSheetSideActions } from '@features/reports/views/NL/balanceSheetReport/components/BalanceSheetSideActions'
import { BalanceSheetFiltersContextProvider } from '@features/reports/views/NL/balanceSheetReport/contexts/balanceSheetFiltersContext'
import { ProfitAndLossSideActions } from '@features/reports/views/NL/profitAndLossReport/components/ProfitAndLossSideActions'
import { ProfitAndLossFiltersContextProvider } from '@features/reports/views/NL/profitAndLossReport/contexts/profitAndLossFiltersContext'
import { TrialBalanceSideActions } from '@features/reports/views/NL/trialBalanceReport/components/TrialBalanceSideActions'
import { TrialBalanceFiltersContextProvider } from '@features/reports/views/NL/trialBalanceReport/contexts/trialBalanceFiltersContext'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useLocalStorageUncontrolled } from '../../hooks/useLocalStorageUncontrolled'
import { getOrganizationViewUrl } from '../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../utils/routing/reactRoute'
import { REPORTS_IDS } from './constants/reportsRouteConfig'
import { REPORTS_ROUTE_STORAGE_KEY } from './constants/reportsRouteStorageKey'
import { ReportsNavigationTabs } from './elements/ReportsNavigationTabs'
import { ReportsSideActions } from './elements/ReportsSideActions'
import { Balance } from './routes/Balance'
import { ProfitAndLoss } from './routes/ProfitAndLoss'
import { TrialBalance } from './routes/TrialBalance'
import { getReportsRouteUrl } from './utils/getReportsToRouteUrl'
import { isRouteValid } from './utils/isRouteValid'

const REPORTS_COMPONENT = {
  [ReportsType.ProfitAndLoss]: ProfitAndLoss,
  [ReportsType.BalanceSheet]: Balance,
  [ReportsType.TrialBalance]: TrialBalance,
}

type ReportsLayoutConfig = {
  Provider: ComponentType<{ children: ReactElement }>
  SideActions: ComponentType
}

const REPORTS_LAYOUT_CONFIG: Record<ReportsType, ReportsLayoutConfig> & { default: ReportsLayoutConfig } = {
  default: {
    Provider: Fragment,
    SideActions: ReportsSideActions,
  },
  [ReportsType.ProfitAndLoss]: {
    Provider: ProfitAndLossFiltersContextProvider,
    SideActions: ProfitAndLossSideActions,
  },
  [ReportsType.BalanceSheet]: {
    Provider: BalanceSheetFiltersContextProvider,
    SideActions: BalanceSheetSideActions,
  },
  [ReportsType.TrialBalance]: {
    Provider: TrialBalanceFiltersContextProvider,
    SideActions: TrialBalanceSideActions,
  },
}

export const Reports = (): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const { getItem } = useLocalStorageUncontrolled(REPORTS_ROUTE_STORAGE_KEY)
  const { organization } = useUserOrganization()

  const storedRoute = getItem()
  const mainRoute = getOrganizationViewUrl(organization?.url, reactRoute.organization.reports.getMainRoute())

  /* Temporary distinction until all reports are updated */
  const isGermanOrganization = organization?.countryId === 'DE'
  const isDutchOrganization = organization?.countryId === 'NL'

  const defaultReportUrl = getReportsRouteUrl(organization?.url, ReportsType.ProfitAndLoss)
  const defaultRoute = storedRoute && isRouteValid(storedRoute, organization?.url) ? storedRoute : defaultReportUrl

  const currentReport = REPORTS_IDS.find((id) => getReportsRouteUrl(organization?.url, id) === location.pathname)

  /* Only show trial balance for DE orgs */
  if (isGermanOrganization) {
    const trialBalanceUrl = getReportsRouteUrl(organization?.url, ReportsType.TrialBalance)

    if (location.pathname !== trialBalanceUrl) {
      return <Navigate to={trialBalanceUrl} replace />
    }

    return (
      <ModuleLayout title={t('reports.trial_balance')} sideActions={<ReportsSideActions />}>
        <TrialBalance />
      </ModuleLayout>
    )
  }

  /* redirect from /reports-all to specific report from local storage or default */
  if (location.pathname === mainRoute) {
    return <Navigate to={defaultRoute} replace />
  }

  /* redirect from /reports-all/{any faulty name} to a default report */
  if (!currentReport) {
    return <Navigate to={defaultReportUrl} replace />
  }

  const layoutConfig = isDutchOrganization ? REPORTS_LAYOUT_CONFIG[currentReport] : REPORTS_LAYOUT_CONFIG.default
  const { Provider, SideActions } = layoutConfig

  return (
    <Provider>
      <ModuleLayout
        fullWidth={isDutchOrganization}
        title={t('reports')}
        headerBottomActions={<ReportsNavigationTabs />}
        sideActions={<SideActions />}
      >
        {createElement(REPORTS_COMPONENT[currentReport])}
      </ModuleLayout>
    </Provider>
  )
}
